import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Paper, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, CircularProgress
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PURPLE = "#244C77";
const SECONDARY = "#4A7BA8";

const STATUS_CHIP = {
  "Escalated to Principal": { color: "#d97706", bg: "#fffbeb" },
  "Principal Approved":     { color: "#16a34a", bg: "#f0fdf4" },
  "Rejected":               { color: "#dc2626", bg: "#fef2f2" },
  "Escalated to Finance":   { color: "#336599", bg: "#f5f3ff" },
};

const FALLBACK_WITHDRAWALS = [
  { id: "1", studentName: "Arjun Rajan",    registrationNo: "CTS-2024-001", grade: "Nilai 4", section: "A", reason: "Relocating to Chennai",   submittedDate: "2025-01-10", status: "Escalated to Principal" },
  { id: "2", studentName: "Priya Sharma",   registrationNo: "CTS-2024-002", grade: "Nilai 6", section: "B", reason: "Family circumstances",    submittedDate: "2025-01-14", status: "Escalated to Principal" },
  { id: "3", studentName: "Karthik Mani",   registrationNo: "CTS-2024-003", grade: "Mazhalai", section: "A", reason: "Health issues",          submittedDate: "2025-01-18", status: "Escalated to Principal" },
  { id: "4", studentName: "Divya Lakshmi",  registrationNo: "CTS-2024-004", grade: "Nilai 3", section: "C", reason: "Transfer to another school", submittedDate: "2025-01-20", status: "Escalated to Principal" },
  { id: "5", studentName: "Suresh Kumar",   registrationNo: "CTS-2024-005", grade: "Nilai 7", section: "A", reason: "Financial constraints",   submittedDate: "2025-01-22", status: "Principal Approved" },
];

const QUICK_LINKS = [
  { label: "Registrations",    path: "principal-registrations",  icon: <AssignmentTurnedInIcon />, color: PURPLE,    bg: "#EAF1F8" },
  { label: "Withdrawals",      path: "principal-withdrawal",      icon: <ExitToAppIcon />,          color: "#dc2626", bg: "#fef2f2" },
  { label: "Leave Approvals",  path: "principal-leave-approvals", icon: <BeachAccessIcon />,        color: "#16a34a", bg: "#f0fdf4" },
  { label: "Staff Management", path: "principal-staff",           icon: <HowToRegIcon />,           color: "#2196f3", bg: "#e3f2fd" },
  { label: "Announcements",    path: "principal-announcements",   icon: <MenuBookIcon />,           color: "#f59e0b", bg: "#fffbeb" },
  { label: "Reports",          path: "principal-reports",         icon: <AdminPanelSettingsIcon />, color: "#336599", bg: "#f5f3ff" },
];

const PrincipalDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading]           = useState(true);
  const [stats, setStats]               = useState({ totalStudents: 0, activeStudents: 0, pendingWithdrawals: 0, pendingLeaves: 0 });
  const [withdrawals, setWithdrawals]   = useState([]);
  const [staffSummary, setStaffSummary] = useState([]);
  const [toast, setToast]               = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("principal/dashboard");
      if (res?.data) {
        const d = res.data;
        setStats({
          totalStudents:    d.totalStudents    ?? 0,
          activeStudents:   d.activeStudents   ?? 0,
          pendingWithdrawals: d.pendingWithdrawals ?? 0,
          pendingLeaves:    d.pendingLeaves    ?? 0,
        });
        setWithdrawals(d.recentWithdrawals?.slice(0, 5) || FALLBACK_WITHDRAWALS.slice(0, 5));
        setStaffSummary(d.staffAttendance || []);
      } else {
        setStats({ totalStudents: 312, activeStudents: 287, pendingWithdrawals: 4, pendingLeaves: 6 });
        setWithdrawals(FALLBACK_WITHDRAWALS);
      }
    } catch (_) {
      setStats({ totalStudents: 312, activeStudents: 287, pendingWithdrawals: 4, pendingLeaves: 6 });
      setWithdrawals(FALLBACK_WITHDRAWALS);
      setToast({ open: true, severity: "warning", message: "Showing demo data — API unavailable" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpiCards = [
    { label: "Total Registered Students",    value: stats.totalStudents,      color: PURPLE,    bg: "#EAF1F8", icon: <PeopleIcon /> },
    { label: "Active Students",              value: stats.activeStudents,     color: "#16a34a", bg: "#f0fdf4", icon: <SchoolIcon /> },
    { label: "Pending Withdrawal Approvals", value: stats.pendingWithdrawals, color: "#dc2626", bg: "#fef2f2", icon: <ExitToAppIcon /> },
    { label: "Pending Leave Approvals",      value: stats.pendingLeaves,      color: "#f59e0b", bg: "#fffbeb", icon: <EventNoteIcon /> },
  ];

  const StatusChip = ({ status }) => {
    const cfg = STATUS_CHIP[status] || { color: "#6B7280", bg: "#f3f4f6" };
    return (
      <Chip
        label={status || "—"}
        size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }}
      />
    );
  };

  return (
    <Box sx={{ p: 3, fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      {/* Page header */}
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Principal Dashboard</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <AdminPanelSettingsIcon sx={{ color: PURPLE, fontSize: 30 }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "22px", color: "#111827" }}>Principal Dashboard</Typography>
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
            Welcome back — here is your school overview for today.
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} mb={3}>
            {kpiCards.map((kpi, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5, borderRadius: 3, backgroundColor: kpi.bg,
                    display: "flex", alignItems: "center", gap: 2,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-2px)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 50, height: 50, borderRadius: 2,
                      backgroundColor: kpi.color + "20",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: kpi.color, flexShrink: 0
                    }}
                  >
                    {kpi.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: kpi.color, lineHeight: 1.1 }}>
                      {kpi.value}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: 0.3 }}>
                      {kpi.label}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {/* Quick Navigation Cards */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`, px: 2.5, py: 2 }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>Quick Navigation</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>Jump to sub-modules</Typography>
                </Box>
                <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {QUICK_LINKS.map((link) => (
                    <Box
                      key={link.label}
                      onClick={() => navigate(link.path)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 1.5, py: 1, borderRadius: 2,
                        backgroundColor: link.bg, cursor: "pointer",
                        border: `1px solid ${link.color}20`,
                        transition: "all 0.2s",
                        "&:hover": { transform: "translateY(-1px)", boxShadow: `0 4px 12px ${link.color}30` },
                        width: "calc(50% - 6px)",
                      }}
                    >
                      <Box sx={{ color: link.color, display: "flex", alignItems: "center" }}>
                        {React.cloneElement(link.icon, { sx: { fontSize: "18px" } })}
                      </Box>
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: link.color }}>
                        {link.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Recent Withdrawal Requests */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`,
                    px: 2.5, py: 2,
                    display: "flex", justifyContent: "space-between", alignItems: "center"
                  }}
                >
                  <Box>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>Recent Withdrawal Requests</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>Escalated to Principal level</Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => navigate("principal-withdrawal")}
                    sx={{ color: "#fff", textTransform: "none", fontSize: "12px", border: "1px solid rgba(255,255,255,0.4)", borderRadius: "8px" }}
                  >
                    View All
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#F8F9FA" }}>
                      <TableRow>
                        {["Student Name", "Reg No", "Grade", "Reason", "Date", "Status"].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px", color: "#374151" }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {withdrawals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#9CA3AF", fontSize: "13px" }}>
                            No escalated withdrawal requests
                          </TableCell>
                        </TableRow>
                      ) : withdrawals.map((w, idx) => (
                        <TableRow key={w.id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                          <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 26, height: 26, fontSize: "11px", bgcolor: PURPLE + "20", color: PURPLE }}>
                                {(w.studentName || "S")[0]}
                              </Avatar>
                              {w.studentName}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>{w.registrationNo}</TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#374151" }}>{w.grade}</TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#374151", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {w.reason}
                          </TableCell>
                          <TableCell sx={{ fontSize: "12px", color: "#6B7280" }}>
                            {w.submittedDate ? new Date(w.submittedDate).toLocaleDateString("en-GB") : "—"}
                          </TableCell>
                          <TableCell><StatusChip status={w.status} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Staff Attendance Summary */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${SECONDARY} 100%)`, px: 2.5, py: 2 }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>Staff Attendance Summary</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>Today's attendance overview by grade</Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                  {staffSummary.length === 0 ? (
                    <Grid container spacing={2}>
                      {[
                        { grade: "MunMazhalai", present: 2, total: 2, color: PURPLE },
                        { grade: "Mazhalai",    present: 3, total: 3, color: SECONDARY },
                        { grade: "Nilai 1-3",   present: 5, total: 6, color: "#2196f3" },
                        { grade: "Nilai 4-6",   present: 6, total: 6, color: "#4caf50" },
                        { grade: "Nilai 7-8",   present: 3, total: 4, color: "#f59e0b" },
                      ].map((s, i) => (
                        <Grid item xs={12} sm={6} md={2.4} key={i}>
                          <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: s.color + "10", border: `1px solid ${s.color}30` }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: s.color, mb: 0.5 }}>{s.grade}</Typography>
                            <Typography sx={{ fontSize: "22px", fontWeight: 700, color: s.color }}>
                              {s.present}<Typography component="span" sx={{ fontSize: "13px", color: "#9CA3AF" }}>/{s.total}</Typography>
                            </Typography>
                            <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#f0f0f0", mt: 1, overflow: "hidden" }}>
                              <Box sx={{ height: "100%", width: `${Math.round((s.present / s.total) * 100)}%`, backgroundColor: s.color, borderRadius: 3 }} />
                            </Box>
                            <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5 }}>
                              {Math.round((s.present / s.total) * 100)}% present
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {staffSummary.map((s, i) => {
                        const pct = s.total > 0 ? Math.round((s.present / s.total) * 100) : 0;
                        const colors = [PURPLE, SECONDARY, "#2196f3", "#4caf50", "#f59e0b"];
                        const c = colors[i % colors.length];
                        return (
                          <Grid item xs={12} sm={6} md={2.4} key={i}>
                            <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: c + "10", border: `1px solid ${c}30` }}>
                              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: c, mb: 0.5 }}>{s.grade}</Typography>
                              <Typography sx={{ fontSize: "22px", fontWeight: 700, color: c }}>
                                {s.present}<Typography component="span" sx={{ fontSize: "13px", color: "#9CA3AF" }}>/{s.total}</Typography>
                              </Typography>
                              <Box sx={{ height: 6, borderRadius: 3, backgroundColor: "#f0f0f0", mt: 1, overflow: "hidden" }}>
                                <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: c, borderRadius: 3 }} />
                              </Box>
                              <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5 }}>{pct}% present</Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default PrincipalDashboard;
